import React, { useCallback, useEffect, useState } from 'react';
import EventBrellaLayout from './EventBrellaLayout';
import { MOBILE_ORDERING_EVENT_STATS } from '../../config/mobileOrderingExperience';
import './EventBrellaTheExperience.css';

const TOTAL_SLIDES = 12;
const {
  eventLabel,
  attendees,
  lineWaitMinutes,
  counterFulfillmentMinutes,
  totalWaitMinutes,
} = MOBILE_ORDERING_EVENT_STATS;

const WORKFLOWS = [
  {
    title: 'Pickup + ready ping',
    staff: 'Low labor',
    guest: 'Order ahead, walk up when ready',
    fit: 'Bars, mid-size venues',
  },
  {
    title: 'Runner to section',
    staff: 'Higher touch',
    guest: 'Order comes to you',
    fit: 'Seated / VIP floor',
  },
  {
    title: 'Locker / pickup shelf',
    staff: 'No-contact',
    guest: 'Grab & go, no queue',
    fit: 'High volume peaks',
  },
  {
    title: 'Hybrid timed windows',
    staff: 'Demand smoothing',
    guest: 'Pickup slot, less crowding',
    fit: 'Spike-heavy nights',
  },
];

const STATUS_STEPS = ['Received', 'Preparing', 'Ready'];

const HARDWARE_ITEMS = [
  {
    title: 'Retractable banner',
    detail: 'Walks guests through mobile ordering — at the entrance / line area',
  },
  {
    title: 'Table-top QR stands',
    detail: 'Frames at each seating or gathering area',
  },
  {
    title: 'Branded handheld POS',
    detail: `${eventLabel} branding on devices at the bar`,
  },
  {
    title: 'End-to-end tech',
    detail: 'Seamlessly handles setup, integration, and support — not just the app',
  },
];

const AI_ITEMS = [
  {
    title: 'For guests',
    detail: 'Personalized recommendations & faster reorders',
  },
  {
    title: 'For staff',
    detail: 'Real-time demand and prep-time patterns',
  },
  {
    title: 'For inventory',
    detail: 'Stock-level and depletion forecasting',
  },
];

const ROLLOUT = [
  { label: 'Devices', detail: '2–3 tablets / phones at bar + one backup' },
  { label: 'Staff training', detail: '15-min run-through: queue, status, pickup callout' },
  { label: 'Timeline', detail: `Pilot at next ${eventLabel} — soft launch one bar` },
  { label: 'Success check', detail: 'Line wait down, orders up, staff feedback' },
];

function SlideQuote({ text, attribution, paraphrase = false }) {
  return (
    <blockquote className={`eb-xp-quote${paraphrase ? ' paraphrase' : ''}`}>
      <p>{paraphrase ? text : `“${text}”`}</p>
      <cite>— {attribution}</cite>
    </blockquote>
  );
}

const EventBrellaTheExperienceInner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(
    () => setCurrentSlide((prev) => Math.min(prev + 1, TOTAL_SLIDES - 1)),
    []
  );
  const prevSlide = useCallback(
    () => setCurrentSlide((prev) => Math.max(prev - 1, 0)),
    []
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  useEffect(() => {
    let touchStartX = 0;
    const onStart = (e) => {
      touchStartX = e.changedTouches[0].clientX;
    };
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) < 50) return;
      if (dx < 0) nextSlide();
      else prevSlide();
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [nextSlide, prevSlide]);

  return (
    <div className="eb-xp">
      <div
        className="eb-xp-track"
        style={{ transform: `translateX(-${currentSlide * 100}vw)` }}
      >
        {/* 1. Title */}
        <section className="eb-xp-slide" aria-label="Title">
          <div className="eb-xp-inner eb-xp-center">
            <span className="eyebrow">{eventLabel} × Seamlessly</span>
            <h1>
              Mobile Ordering:
              <span className="foil"> Killing the Line</span>
            </h1>
            <p className="eb-xp-lede">
              Average wait time at {eventLabel} — {attendees} guests. The bar line was the bottleneck.
            </p>
            <div className="eb-xp-hero-stat">
              <div className="eb-xp-big">{totalWaitMinutes}</div>
              <div className="eb-xp-big-unit">minutes</div>
              <p className="eb-xp-big-cap">decide to order → order in hand</p>
            </div>
            <SlideQuote
              text="Quality, Service, Cleanliness and Value."
              attribution="Ray Kroc (McDonald's motto)"
            />
          </div>
        </section>

        {/* 2. The Problem */}
        <section className="eb-xp-slide" aria-label="The Problem">
          <div className="eb-xp-inner">
            <span className="eyebrow">The Problem</span>
            <h2>{totalWaitMinutes} minutes average wait</h2>
            <SlideQuote
              text="You've got to start with the customer experience and work backwards to the technology."
              attribution="Steve Jobs"
            />
            <div className="eb-xp-stat-row">
              <div className="eb-xp-stat-card">
                <div className="eb-xp-stat-num">{lineWaitMinutes}</div>
                <div className="eb-xp-stat-label">min in line</div>
              </div>
              <div className="eb-xp-stat-plus">+</div>
              <div className="eb-xp-stat-card">
                <div className="eb-xp-stat-num">{counterFulfillmentMinutes}</div>
                <div className="eb-xp-stat-label">min at counter</div>
              </div>
              <div className="eb-xp-stat-plus">=</div>
              <div className="eb-xp-stat-card accent">
                <div className="eb-xp-stat-num foil">{totalWaitMinutes}</div>
                <div className="eb-xp-stat-label">total dead time</div>
              </div>
            </div>
            <ul className="eb-xp-bullets">
              <li>Lost time</li>
              <li>Lost sales</li>
              <li>Frustrated guests</li>
            </ul>
          </div>
        </section>

        {/* 3. What's Actually Happening */}
        <section className="eb-xp-slide" aria-label="What's Actually Happening">
          <div className="eb-xp-inner">
            <span className="eyebrow">During Those {lineWaitMinutes} Minutes</span>
            <h2>What&apos;s actually happening</h2>
            <SlideQuote
              text="Lost time is never found again."
              attribution="Benjamin Franklin"
            />
            <div className="eb-xp-pillars">
              <div className="eb-xp-pillar">
                <h3>Not bowling</h3>
                <p>Pulled out of the guest journey — the activity they came for.</p>
              </div>
              <div className="eb-xp-pillar">
                <h3>Not with their group</h3>
                <p>Standing alone in line instead of with the people they came with.</p>
              </div>
              <div className="eb-xp-pillar">
                <h3>Not enjoying the event</h3>
                <p>Every minute in line is a minute stolen from the night.</p>
              </div>
            </div>
            <p className="eb-xp-kicker">
              Frustration in the line risks getting associated with the event itself —
              the opposite of a memorable night out.
            </p>
          </div>
        </section>

        {/* 4. The Fix */}
        <section className="eb-xp-slide" aria-label="The Fix">
          <div className="eb-xp-inner eb-xp-center">
            <span className="eyebrow">The Fix</span>
            <h2>
              Order from your <span className="foil">phone</span>
            </h2>
            <SlideQuote
              text="Starbucks represents something beyond a cup of coffee."
              attribution="Howard Schultz"
            />
            <p className="eb-xp-lede eb-xp-lede-lg">
              Order ahead. Skip the line. No dead time.
            </p>
            <div className="eb-xp-flow">
              <span>Browse</span>
              <span className="eb-xp-flow-arrow">→</span>
              <span>Pay</span>
              <span className="eb-xp-flow-arrow">→</span>
              <span>Get pinged</span>
              <span className="eb-xp-flow-arrow">→</span>
              <span>Pick up</span>
            </div>
          </div>
        </section>

        {/* 5. Workflow Options */}
        <section className="eb-xp-slide" aria-label="Workflow Options">
          <div className="eb-xp-inner">
            <span className="eyebrow">Workflow Options</span>
            <h2>Four ways to kill the line</h2>
            <SlideQuote
              text="Efficiency is doing things right. Effectiveness is doing the right things."
              attribution="Peter Drucker"
            />
            <div className="eb-xp-workflow-grid">
              {WORKFLOWS.map((w) => (
                <article key={w.title} className="eb-xp-workflow">
                  <h3>{w.title}</h3>
                  <ul>
                    <li><strong>Staff</strong> {w.staff}</li>
                    <li><strong>Guest</strong> {w.guest}</li>
                    <li><strong>Best fit</strong> {w.fit}</li>
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Recommended Approach */}
        <section className="eb-xp-slide" aria-label="Recommended Approach">
          <div className="eb-xp-inner">
            <span className="eyebrow">Recommended</span>
            <h2>Pickup-at-counter + status ping</h2>
            <SlideQuote
              text="Perfection is finally attained not when there is no longer anything to add, but when there is no longer anything to take away."
              attribution="Antoine de Saint-Exupéry"
            />
            <div className="eb-xp-status">
              {STATUS_STEPS.map((step, i) => (
                <React.Fragment key={step}>
                  <div className="eb-xp-status-step">
                    <span className="eb-xp-status-num">{i + 1}</span>
                    <span>{step}</span>
                  </div>
                  {i < STATUS_STEPS.length - 1 ? (
                    <span className="eb-xp-flow-arrow" aria-hidden>→</span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
            <ul className="eb-xp-bullets">
              <li>Right for this venue size — no runners required</li>
              <li>Staff keep control of pace</li>
              <li>Guests stay in the room, not the queue</li>
              <li>Lowest ops lift to pilot next event</li>
            </ul>
          </div>
        </section>

        {/* 7. For the Staff */}
        <section className="eb-xp-slide" aria-label="For the Staff">
          <div className="eb-xp-inner">
            <span className="eyebrow">For the Staff</span>
            <h2>A staff win — not just a guest one</h2>
            <SlideQuote
              text="Hospitality is almost impossible to teach. It's all about hiring the right people."
              attribution="Danny Meyer"
            />
            <ul className="eb-xp-bullets eb-xp-bullets-lg">
              <li>No endless line to manage while fulfilling orders</li>
              <li>Orders queue digitally — prep at your pace</li>
              <li>No crowd control at the rail</li>
              <li>Focus on the craft, not the crush</li>
            </ul>
          </div>
        </section>

        {/* 8. For the Venue */}
        <section className="eb-xp-slide" aria-label="For the Venue">
          <div className="eb-xp-inner">
            <span className="eyebrow">For the Venue</span>
            <h2>Capture what the line kills</h2>
            <SlideQuote
              text="There is only one valid definition of business purpose: to create a customer."
              attribution="Peter Drucker"
            />
            <div className="eb-xp-pillars">
              <div className="eb-xp-pillar">
                <h3>Abandoned → paid</h3>
                <p>
                  Orders that would walk away from a {lineWaitMinutes}-minute wait get captured on the phone.
                </p>
              </div>
              <div className="eb-xp-pillar">
                <h3>Faster turnover</h3>
                <p>More transactions per hour at the same footprint.</p>
              </div>
              <div className="eb-xp-pillar">
                <h3>More revenue / event</h3>
                <p>Every minute not waiting is a minute they could order again.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 9. For the Host */}
        <section className="eb-xp-slide" aria-label="For the Host">
          <div className="eb-xp-inner">
            <span className="eyebrow">For the Host / Organizer</span>
            <h2>Happier guests. Better nights.</h2>
            <SlideQuote
              text="We see our customers as invited guests to a party."
              attribution="Jeff Bezos"
            />
            <ul className="eb-xp-bullets eb-xp-bullets-lg">
              <li>More time enjoying the event — less time in line</li>
              <li>Stronger reviews & event NPS</li>
              <li>Repeat attendance follows the experience</li>
              <li>Your brand feels seamless — not chaotic</li>
            </ul>
          </div>
        </section>

        {/* 10. VIP Tier */}
        <section className="eb-xp-slide" aria-label="VIP Tier">
          <div className="eb-xp-inner">
            <span className="eyebrow">VIP Tier</span>
            <h2>
              Skip-the-line as a <span className="foil">premium upsell</span>
            </h2>
            <SlideQuote
              text="The greatest luxury for our customers was time."
              attribution="Isadore Sharp (Four Seasons)"
            />
            <div className="eb-xp-pillars">
              <div className="eb-xp-pillar">
                <h3>Priority queue</h3>
                <p>VIP tickets jump the digital line — orders first.</p>
              </div>
              <div className="eb-xp-pillar">
                <h3>Dedicated pickup</h3>
                <p>Separate callout point. Feels exclusive. Moves fast.</p>
              </div>
              <div className="eb-xp-pillar">
                <h3>New revenue line</h3>
                <p>Ties to Seamlessly ticketing tiers — guests pay for access.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 11. What You Get */}
        <section className="eb-xp-slide" aria-label="What You Get">
          <div className="eb-xp-inner">
            <span className="eyebrow">What You Get</span>
            <h2>The full package</h2>
            <SlideQuote
              text="We can take full responsibility for the user experience."
              attribution="Steve Jobs"
            />
            <p className="eb-xp-tier-label">Hardware &amp; Setup</p>
            <div className="eb-xp-hardware-grid">
              {HARDWARE_ITEMS.map((item) => (
                <article key={item.title} className="eb-xp-package-item">
                  <h3>{item.title}</h3>
                  <p>{item.detail}</p>
                </article>
              ))}
            </div>
            <div className="eb-xp-ai-band">
              <div className="eb-xp-ai-band-head">
                <span className="eb-xp-tier-label ai">AI-Powered Ordering</span>
                <p>The smart layer — insight for every seat at the table.</p>
              </div>
              <div className="eb-xp-ai-cols">
                {AI_ITEMS.map((item) => (
                  <div key={item.title} className="eb-xp-ai-col">
                    <h3>{item.title}</h3>
                    <p>{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 12. Rollout */}
        <section className="eb-xp-slide" aria-label="Rollout">
          <div className="eb-xp-inner">
            <span className="eyebrow">Next Steps</span>
            <h2>Pilot at the next event</h2>
            <SlideQuote
              paraphrase
              text="People will forget what you said and what you did, but they will never forget how you made them feel."
              attribution="Maya Angelou (paraphrase)"
            />
            <div className="eb-xp-rollout">
              {ROLLOUT.map((item) => (
                <div key={item.label} className="eb-xp-rollout-row">
                  <span className="eb-xp-rollout-label">{item.label}</span>
                  <span className="eb-xp-rollout-detail">{item.detail}</span>
                </div>
              ))}
            </div>
            <p className="eb-xp-kicker">Kill the line. Keep the night moving.</p>
          </div>
        </section>
      </div>

      {currentSlide > 0 && (
        <button
          type="button"
          className="eb-xp-nav eb-xp-nav-prev"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          ‹
        </button>
      )}
      {currentSlide < TOTAL_SLIDES - 1 && (
        <button
          type="button"
          className="eb-xp-nav eb-xp-nav-next"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          ›
        </button>
      )}

      <div className="eb-xp-counter" aria-live="polite">
        {currentSlide + 1} / {TOTAL_SLIDES}
      </div>

      <div className="eb-xp-dots" role="tablist" aria-label="Slides">
        {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === currentSlide}
            className={`eb-xp-dot${i === currentSlide ? ' active' : ''}`}
            onClick={() => setCurrentSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const EventBrellaTheExperience = () => (
  <EventBrellaLayout deck>
    <EventBrellaTheExperienceInner />
  </EventBrellaLayout>
);

export default EventBrellaTheExperience;
